import { useCallback, useEffect, useRef, useState } from "react";

import { getMenuItems } from "services/menu/rest";
import { MenuItemListDto } from "services/menu/restModels";

import { STOP_LIST_PAGE_SIZE } from "features/StopList/constants";

export interface UseStopListItemsParams {
    menuId: number;
    query: string;
    categoryId: number | null;
    page: number;
    // На вкладке категорий страница позиций не видна — не тянем её и не гасим уже полученные
    // данные: возврат на вкладку должен быть мгновенным.
    enabled: boolean;
}

export interface StopListItemsState {
    items: MenuItemListDto[];
    total: number;
    loading: boolean;
    error: boolean;
    reload: () => void;
    removeItem: (menuItemId: number) => void;
}

export const useStopListItems = (params: UseStopListItemsParams): StopListItemsState => {
    const { menuId, query, categoryId, page, enabled } = params;

    const [items, setItems] = useState<MenuItemListDto[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [reloadKey, setReloadKey] = useState<number>(0);

    // Номер актуальной выборки: ответ по прошлому запросу отбрасываем, иначе медленная
    // первая страница дорисовалась бы поверх уже открытой второй.
    const scopeRef = useRef<number>(0);

    const reload = useCallback(() => setReloadKey((key) => key + 1), []);

    const removeItem = useCallback((menuItemId: number) => {
        setItems((current) => current.filter((item) => item.id !== menuItemId));
        setTotal((current) => Math.max(0, current - 1));
    }, []);

    useEffect(() => {
        if (!enabled) return;

        const scope = scopeRef.current + 1;
        scopeRef.current = scope;

        setLoading(true);
        setError(false);

        getMenuItems({
            menuId,
            categoryId: categoryId ?? undefined,
            query: query || undefined,
            filter: "stopList",
            skip: (page - 1) * STOP_LIST_PAGE_SIZE,
            limit: STOP_LIST_PAGE_SIZE,
        })
            .then((result) => {
                if (scopeRef.current !== scope) return;

                if (!result) {
                    setError(true);
                    setItems([]);
                    setTotal(0);
                    return;
                }

                setItems(result.items);

                // total бэк отдаёт только на первой странице — это свойство выборки, а не
                // страницы. Смена поиска, фильтра или таба возвращает на первую страницу,
                // поэтому к моменту листания он уже получен и остаётся верным.
                if (result.total !== null) setTotal(result.total);
            })
            .catch(() => {
                if (scopeRef.current !== scope) return;
                setError(true);
                setItems([]);
                setTotal(0);
            })
            .finally(() => {
                if (scopeRef.current !== scope) return;
                setLoading(false);
            });
    }, [menuId, query, categoryId, page, enabled, reloadKey]);

    return { items, total, loading, error, reload, removeItem };
};
